// ─── Utility helpers ────────────────────────────────────────────────────────

import {
  MOCK_ANALYSIS_RESULT,
  type AnalysisResult,
} from "../mockdata/analysisData";

/** Returns Chakra colorPalette token based on a 0-100 score */
export const getScoreColor = (score: number): string => {
  if (score >= 80) return "green";
  if (score >= 60) return "yellow";
  if (score >= 40) return "orange";
  return "red";
};

export type ScoreLabelKey = "excellent" | "good" | "fair" | "needsWork";

/** Returns a translation key for a 0–100 score label */
export const getScoreLabelKey = (score: number): ScoreLabelKey => {
  if (score >= 80) return "excellent";
  if (score >= 60) return "good";
  if (score >= 40) return "fair";
  return "needsWork";
};

/** @deprecated Use getScoreLabelKey + t(`scoreLabel.${key}`) */
export const getScoreLabel = getScoreLabelKey;

/** Returns badge color for improvement priority */
export const getPriorityColor = (
  priority: "high" | "medium" | "low",
): string => {
  switch (priority) {
    case "high":
      return "red";
    case "medium":
      return "orange";
    case "low":
      return "blue";
  }
};

/** Format file size */
export const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Sanitize + title-case a candidate name for display.
 * Keeps Unicode letters (incl. Vietnamese), numbers, spaces, hyphen, apostrophe;
 * strips other special characters; capitalizes each word.
 * I.e: "JOHN DOE" -> "John Doe"
 * I.e: "john, doe" -> "John Doe"
 */
export function formatCandidateName(
  name: string | null | undefined,
): string | null {
  if (name == null) return null;

  const cleaned = name
    .normalize("NFC")
    .replace(/[^\p{L}\p{M}\p{N}\s'-]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!cleaned) return null;

  return cleaned
    .split(" ")
    .map((word) =>
      word
        .split(/(['-])/)
        .map((part) => {
          if (part === "'" || part === "-") return part;
          return (
            part.charAt(0).toLocaleUpperCase("vi") +
            part.slice(1).toLocaleLowerCase("vi")
          );
        })
        .join(""),
    )
    .join(" ");
}

/** Validate accepted file types */
export const isValidResumeFile = (file: File): boolean => {
  const accepted = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  ];
  return accepted.includes(file.type);
};

/** Simulate API call — replace with real implementation */
export const analyzeResume = async (file: File): Promise<AnalysisResult> => {
  // TODO: Replace with real API call
  // const formData = new FormData();
  // formData.append("resume", file);
  // const res = await fetch("/api/resume-analysis", { method: "POST", body: formData });
  // return res.json() as Promise<AnalysisResult>;
  void file;
  await new Promise((resolve) => setTimeout(resolve, 3000));
  return MOCK_ANALYSIS_RESULT;
};
